













































































































































import Card from '@/components/Card.vue';
import {
  ProvenanceNode,
  InformationField,
  uniqueId,
  Study,
  NodeDefinition,
  makeLookup,
} from 'common';
import { createComponent, computed, watch } from '@vue/composition-api';
import { getLabel } from '../utils';

const sentRequests: Record<string, boolean> = {};

export default createComponent({
  name: 'NodeFormCard',
  components: { Card },
  props: {
    node: { type: Object as () => ProvenanceNode, required: true },
    definition: {
      type: Object as () => NodeDefinition | undefined,
      required: false,
    },
    fields: { type: Array as () => InformationField[], required: true },
    studies: { type: Array as () => Study[], required: true },
    definitions: { type: Array as () => NodeDefinition[], required: true },
    nodes: { type: Array as () => ProvenanceNode[], required: true },
    getLabel: {
      type: Function as unknown as () => (node: ProvenanceNode) => string,
      required: true,
    },
    nodeToRelate: { type: Object as () => ProvenanceNode, required: false },
  },
  setup(props, context) {
    const fieldsLookup = computed((): Record<string, InformationField> => {
      const lookup: Record<string, InformationField> = {};
      props.fields.forEach((field) => (lookup[field.key] = field));
      return lookup;
    });

    const definedFields = computed((): string[] =>
      props.definition
        ? props.definition.informationFields
          ? props.definition.informationFields.map(
              (field) => field.split(',')[0],
            )
          : []
        : [],
    );

    // See schemas.ts for more informatio about "informationFields"
    // Basically, these fileds are defined and can either be a text field
    // or an dropdown with options
    const definedFieldsOptions = computed(() => {
      if (!props.definition || !props.definition.informationFields) {
        return {};
      }
      const lookup: Record<string, string[] | undefined> = {};
      props.definition.informationFields.forEach((field) => {
        const [fieldName, ...options] = field.split(',');
        if (options.length === 0) {
          return;
        }
        lookup[fieldName] = options;
      });

      return lookup;
    });

    const extraFields = computed((): InformationField[] =>
      props.fields.filter((field) => !definedFields.value.includes(field.key)),
    );

    function updateKey(index: number, newValue: string) {
      updateInformationNode(props.fields[index], 'key', newValue);
    }

    function updateValue(index: number, newValue: string) {
      updateInformationNode(props.fields[index], 'value', newValue);
    }

    function addField() {
      const information = {
        id: uniqueId(),
        key: '',
        value: '',
      };

      context.emit('update:information:add', information);
    }

    function deleteField(j: number) {
      context.emit('update:information:delete', props.fields[j]);
    }

    function labelChange(value: string) {
      updateNode('label', value);
    }

    function clearRelatedTo() {
      updateNode('relatedTo', '');
    }

    function definitionChange(definitionId: string) {
      updateNode('definitionId', definitionId);
    }

    function studyIdChange(value: string | undefined) {
      updateNode('studyId', value);
    }

    function updateNode<K extends keyof ProvenanceNode>(
      key: K,
      value: ProvenanceNode[K],
    ) {
      context.emit('update:node', props.node, key, value);
    }

    function updateInformationNode<K extends keyof InformationField>(
      information: InformationField,
      key: K,
      value: InformationField[K],
    ) {
      context.emit('update:information', information, key, value);
    }

    // Updated a defined "informationField"
    // Initially, this information field won't actually exist in the backend
    // But as you can see, we are lazily creating the node using a simple conditional
    function updateDefinedKey(key: string, newValue: string) {
      // If the field isn't defined yet (ie. not in the DB)
      let informationNode = fieldsLookup.value[key];

      // Ok so a bit of a hack but this ensures that we only send *one* request for this node
      // We assume that this node will be created quite quickly
      // As the user continues to edit the value, the node will be updated instead
      // Since we have debouncing, things *should* be ok
      // There is a small chance that an edit request somehow finishes *before* the create request
      // Note that the handler of "update:information:add" also adds the the information node array
      // Sooo "informationNode" should be defined within milliseconds
      // From testing, it seems like this logic works well enough
      const id = props.node.id + '///' + key;
      if (!informationNode && !sentRequests[id]) {
        sentRequests[id] = true;

        // After 10000 seconds, set to false again
        // Things *should* have returned by then
        setTimeout(() => {
          sentRequests[id] = false;
        }, 5000);

        informationNode = {
          id: uniqueId(),
          key,
          value: newValue,
        };

        context.emit('update:information:add', informationNode);
      } else {
        updateInformationNode(informationNode, 'value', newValue);
      }
    }

    const relatedToLabel = computed(() => {
      // console.log(`Related to label: ${props.node.id} -> ${props.node.relatedTo}`)
      const n = props.node.relatedTo
        ? props.nodes.find((node) => node.id === props.node.relatedTo)
        : undefined;
      return n ? props.getLabel(n) : undefined;
    });

    return {
      deleteField,
      labelChange,
      definitionChange,
      studyIdChange,
      updateNode,
      addField,
      updateValue,
      updateKey,
      definedFields,
      extraFields,
      fieldsLookup,
      updateDefinedKey,
      definedFieldsOptions,
      relatedToLabel,
      clearRelatedTo,
    };
  },
});
